import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { masterColumnDefs, masterRowData } from '../../json/PlanningGridData';
import { useBulkActionContext } from '../BulkActionContext';
import { BulkActionHeader } from '../BulkActionHeader';
import {
  ApproverCellRenderer,
  CompensationCycleCellRenderer,
  BUDGET_STATUS_CONFIG,
  BudgetStatusCellRenderer,
  BudgetBarCellRenderer,
} from '../cellRenderers/PlanningGridCellRenderer';
import { PlanningGridDetails } from './PlanningDetailsGrid';

const MASTER_GRID_COLUMN_CELL_RENDERERS = {
  APPROVER: ApproverCellRenderer,
  COMPENSATION_CYCLE: CompensationCycleCellRenderer,
  BUDGET_STATUS: BudgetStatusCellRenderer,
  BUDGET_BAR: BudgetBarCellRenderer,
};

const MASTER_GRID_COLUMN_ADDITIONAL_PROPS = {
  APPROVER: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.APPROVER?.displayName,
    },
  },
  COMPENSATION_CYCLE: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.COMPENSATION_CYCLE,
    },
  },
  BUDGET_STATUS: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        BUDGET_STATUS_CONFIG[params?.data?.BUDGET_STATUS]?.label || params?.data?.BUDGET_STATUS,
    },
  },
  BUDGET_BAR: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.BUDGET_BAR?.totalBudget?.value,
    },
  },
};

export const PlanningMasterGrid = () => {
  const gridRef = useRef(null);

  const columnDefs = useMemo<ColDef[]>(
    () =>
      masterColumnDefs.map((col, index) => {
        const colDef: ColDef = {
          colId: col.columnId,
          headerComponent: CustomHeaderCell,
          sortable: true,
          field: col.columnType,
          headerName: col.displayName,
          cellRenderer: MASTER_GRID_COLUMN_CELL_RENDERERS[col.columnType] || ((params) => <Box>{params.value}</Box>),
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          flex: 1,
          ...(MASTER_GRID_COLUMN_ADDITIONAL_PROPS[col.columnType] || {}),
        };

        if (col.columnType === 'BUDGET_BAR') {
          colDef.minWidth = 250;
        }

        if (index === 0) {
          return {
            ...colDef,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: colDef.cellRenderer,
            },
          };
        }

        return colDef;
      }),
    []
  );

  const { setSelectedRowIds, resetSelection, setTotalRows } = useBulkActionContext();

  const handleSelectionChanged = useCallback(
    (event) => {
      const selectedRows = event.api.getSelectedRows();
      setSelectedRowIds(selectedRows.map((row) => row.id || row.EMPLOYEE?.id || row.APPROVER_NAME?.id));
    },
    [setSelectedRowIds]
  );

  const handleClearSelection = useCallback(() => {
    const api = gridRef.current?.api as GridApi | undefined;
    api?.deselectAll();
  }, [gridRef]);

  const handleSelectAll = useCallback(() => {
    const api = gridRef.current?.api as GridApi | undefined;
    api?.selectAll('filtered');
  }, [gridRef]);

  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    const api = gridRef.current?.api as GridApi | undefined;

    if (api?.isAnyFilterPresent()) {
      api.forEachNodeAfterFilter(() => {
        total += 1;
      });
    } else {
      total = masterRowData?.length || 0;
    }
    setTotalRows(total);
  }, [gridRef, setTotalRows]);

  const onRowDataUpdated = useCallback(() => {
    resetSelection();
    handleClearSelection();
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange, resetSelection, handleClearSelection]);

  useEffect(() => {
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange]);

  const handleAddRow = useCallback(() => {
    console.log('Add new row');
  }, []);

  return (
    <>
      <BulkActionHeader
        clearSelection={handleClearSelection}
        selectAll={handleSelectAll}
        onAddRow={handleAddRow}
        activeTab="publish"
      />
      <Box mt={3}>
        <Box>
          <AgGridTable
            className="compensation-table compensation-planning-grid"
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={masterRowData}
            domLayout="autoHeight"
            pagination
            kebabMenuIsRowSpecific={false}
            paginationPageSize={10}
            headerHeight={36}
            rowHeight={88}
            height="100%"
            masterDetail
            detailRowAutoHeight
            detailCellRenderer={PlanningGridDetails}
            rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
            onSelectionChanged={handleSelectionChanged}
            onRowDataUpdated={onRowDataUpdated}
            onFilterChanged={onRowDataUpdated}
            getRowStyle={(params) => {
              const { node } = params;
              const { expanded } = node;
              if (expanded) return { backgroundColor: '#fafafa' };
              return {};
            }}
            onRowGroupOpened={(params) => params.api.redrawRows({ rowNodes: [params.node] })}
            sideBar={null}
            showSidebarToggle={false}
          />
        </Box>
      </Box>
    </>
  );
};
